import {Inject, Injectable, LOCALE_ID, OnDestroy} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ClubService} from './club.service';
import {Website} from '../models';
import {AuthenticatedUser, Club, CustomerSearchHistory, JWTToken, Module, Webhook} from '../models';
import {BasicStatusTranslationService, ProductSettingsTranslationService} from 'src/app/shared/translations';


/**
 * Class to operate with stored data
 */
@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class StorageService implements OnDestroy {
    public selectedClub: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public castSelectedClub: any = this.selectedClub.asObservable();

    public token: BehaviorSubject<JWTToken> = new BehaviorSubject<JWTToken>(null);
    public castToken: Observable<JWTToken> = this.token.asObservable();

    public authenticatedUser: BehaviorSubject<AuthenticatedUser> = new BehaviorSubject<any>(null);
    public castAuthenticatedUser: Observable<AuthenticatedUser> = this.authenticatedUser.asObservable();

    public currentCountries: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public castCurrentCountries: any = this.currentCountries.asObservable();

    public customerSearchHistory: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public castCustomerSearchHistory: any = this.customerSearchHistory.asObservable();

    public redirectUrl: string;

    constructor(
        @Inject(LOCALE_ID)
        private locale: string,
        private clubService: ClubService,
        private statusTranslations: BasicStatusTranslationService,
        private productTranslations: ProductSettingsTranslationService
    ) {
        this.loadToken();
        this.loadAuthenticatedUser();
        this.loadCustomerSearchHistory();
        this.loadCurrentCountries(`${this.locale}-countries`);
    }

    public ngOnDestroy(): void {
    }

    get selectedClubValue(): Club {
        return this.selectedClub.getValue();
    }

    get authenticatedUserValue(): AuthenticatedUser {
        return this.authenticatedUser.getValue();
    }

    /**
     * Checks if storage item is not empty
     */
    public checkIfNotEmpty(storageItem: any, path?: any): boolean {
        return localStorage.getItem(storageItem) !== null;
    }

    /**
     * Update the selected club-overview
     */
    public updateSelectedClub(club: Club): void {
        this.selectedClub.next(club);
        const user = this.authenticatedUser.value;

        const calls: Array<undefined | Observable<any>> = [];

        /**
         * Modules needs to be fetched since they are a core part of the application
         */
        if (club.modules === null) {
            calls.push(this.clubService.getModules(club.id).pipe(untilDestroyed(this), catchError(error => of(null))));
        }
        /**
         * Webhooks are fetched if the user is an admin to provie the options to edit those
         */
        if (club.webhooks === null && user?.isAdmin) {
            calls.push(this.clubService.getWebhooks(club.id).pipe(untilDestroyed(this), catchError(error => of(null))));
        }
        /**
         * Logo is also fetched if it does not exist yet since it is displayed in the middle of header
         */
        if (club.pathToLogo && typeof club.logo === 'string') {
            calls.push(this.clubService.getPathToImageByMediaObject(club.logo).pipe(untilDestroyed(this), catchError(error => of(null))));
        }
        /**
         * Website data is a needed set of data if TixxSelect is enabled, however we don't necessarily know at the beginning
         * if the TixxSelect module is activated, since we might still be fetching module data.
         * This function is here in case we DO already have the modules loaded through the isLotteryActive property
         */
        if (club.website === null && (user?.isAdmin || user?.isClubAdmin) && club.isLotteryActive) {
            calls.push(this.clubService.getWebsite(club.id).pipe(untilDestroyed(this), catchError(error => of(null))));
        }

        forkJoin(calls).subscribe((results: any) => {
            for (const data of results) {
                if (data['@context'] === '/api/contexts/TixxConnectWebhook') {
                    const webhooks = [];
                    data['hydra:member'].forEach(el => webhooks.push(new Webhook(el, this.statusTranslations, this.productTranslations)));
                    club.webhooks = webhooks;
                } else if (data['@context'] === '/api/contexts/Logo') {
                    club.pathToLogo = data.contentUrl;
                } else if (data[0] instanceof Module) {
                    club.modules = data;
                    /**
                     * We also need to fetch the website in case the lottery module is active.
                     * The website call should not be duplicated, because if we are here in this function,
                     * that means that the above check of isLotteryActive failed because modules were not loaded yet.
                     */
                    if (club.website === null && (user?.isAdmin || user?.isClubAdmin) && club.isLotteryActive) {
                        this.clubService.getWebsite(club.id).pipe(untilDestroyed(this)).subscribe(website => club.website = website);
                    }
                } else if (data instanceof Website) {
                    club.website = data;
                }
            }
            this.selectedClub.next(club);
        });
    }

    /**
     * Clear the selected club-overview
     */
    public clearSelectedClub(): void {
        this.selectedClub.next(null);
    }

    /**
     * Load the selected user on app load
     */
    public loadToken(): void {
        this.token.next(JSON.parse(localStorage.getItem('token')));
    }

    /**
     * Load the search history
     */
    public loadCustomerSearchHistory(): void {
        this.customerSearchHistory.next(JSON.parse(localStorage.getItem('customerSearchHistory')));
    }

    /**
     * Set search history
     */
    public setCustomerSearchHistory(searchHistory: any, clubId: any): void {
        let currentLocalStorageSH = JSON.parse(localStorage.getItem('customerSearchHistory'));
        if (!currentLocalStorageSH) {
            currentLocalStorageSH = {};
        }
        currentLocalStorageSH[clubId] = searchHistory;
        localStorage.setItem('customerSearchHistory', JSON.stringify(currentLocalStorageSH));
        this.customerSearchHistory.next(JSON.parse(localStorage.getItem('customerSearchHistory')));
    }

    /**
     * Remove the current user
     */
    public removeAuthenticatedUser(): void {
        this.token.next(null);
        this.authenticatedUser.next(null);
    }

    /**
     * Set the curently authenticated user
     */
    public setAuthenticatedUser(user: AuthenticatedUser): void {
        localStorage.setItem('authenticatedUser', JSON.stringify(user));
        this.authenticatedUser.next(user);
    }

    /**
     * Load the currently authenticated user
     */
    public loadAuthenticatedUser(): void {
        const parsedUser = JSON.parse(localStorage.getItem('authenticatedUser'));
        let authenticatedUser = null;

        if (parsedUser !== null) {
            authenticatedUser = new AuthenticatedUser(parsedUser, parsedUser.permissions);
        }

        this.authenticatedUser.next(authenticatedUser);
    }

    /**
     * Load countries
     */
    public loadCurrentCountries(path: any): void {
        this.currentCountries.next(JSON.parse(localStorage.getItem(path)));
    }

    /**
     * Set current token
     */
    public setToken(jwtToken: JWTToken): void {
        localStorage.setItem('token', JSON.stringify(jwtToken));

        const parsedUser = JSON.parse(localStorage.getItem('authenticatedUser'));
        if (parsedUser !== null) {
            const authenticatedUser = new AuthenticatedUser(parsedUser, parsedUser.permissions);
            authenticatedUser.setJWTToken = jwtToken;
            localStorage.setItem('authenticatedUser', JSON.stringify(authenticatedUser));
        }

        this.token.next(jwtToken);
    }

    /**
     * Set countries
     */
    public setCurrentCountries(countries: any, path?: any): void {
        localStorage.setItem(path, JSON.stringify(countries));
        this.loadCurrentCountries(path);
    }

    /**
     * Remove countries
     */
    public removeCountries(): void {
        this.currentCountries.next(null);
        for (const country of ['en', 'de', 'es']) {
            localStorage.removeItem(`${country}-countries`);
        }
    }

    /**
     * Is user logged in
     */
    public isLoggedIn(): boolean {
        return this.authenticatedUser.getValue() !== null;
    }

    /**
     * Does user have countries loaded
     */
    public hasCountries(): boolean {
        return this.currentCountries.getValue() !== null;
    }

    /**
     * Adds / removes search values to the local storage.
     */
    public addToLocalStorage(
        query: string,
        values: any,
        basic: boolean,
        searchHistory: Array<CustomerSearchHistory>): void {
        const date: Date = new Date();
        if (searchHistory !== null) {
            const maxArrayLength = 5;
            searchHistory.forEach((value, index) => {
                if (value.query === query) {
                    searchHistory.splice(index, 1);
                }
            });

            if (searchHistory.length < maxArrayLength) {
                searchHistory.unshift(new CustomerSearchHistory(query, values, basic, date));
                this.setCustomerSearchHistory(searchHistory, this.selectedClub.value.id);
            } else {
                searchHistory = searchHistory.slice(0, -1);
                searchHistory.unshift(new CustomerSearchHistory(query, values, basic, date));
                this.setCustomerSearchHistory(searchHistory, this.selectedClub.value.id);
            }
        } else {
            searchHistory.push(new CustomerSearchHistory(query, values, basic, date));
            this.setCustomerSearchHistory(searchHistory, this.selectedClub.value.id);
        }
    }
}
